/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
  getHolzAnhaenger,
  createHolzAnhaenger,
  updateHolzAnhaenger,
  deleteHolzAnhaenger,
  getGravurfarben,
  getRohlingfarben,
  buildAssetUrl
} from '../services/api';
import './HolzAnhaengerVerwaltung.css';

const initialForm = {
  name: '',
  width: 100,
  height: 100,
  scale: 1,
  gravurX: 5,
  gravurY: 5,
  gravurWidth: 40,
  gravurHeight: 20,
  svgFiles: [],
  svgPreview: '',
  kategorie: 'Hunde',
  gravurfarbe: '',
  farben: []
};

function HolzAnhaengerVerwaltung() {
  const [items, setItems] = useState([]);
  const [rohlingfarben, setRohlingfarben] = useState([]);
  const [gravurfarben, setGravurfarben] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [dragState, setDragState] = useState(null); // { mode: 'move'|'resize', startX, startY, startBox }
  const [previewCache, setPreviewCache] = useState({});
  const [coloredFormPreview, setColoredFormPreview] = useState(null);
  const [editSvgPaths, setEditSvgPaths] = useState([]);

  const previewScale = 4; // px per mm for preview

  useEffect(() => {
    loadItems();
    loadRohlingfarben();
    loadGravurfarben();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await getHolzAnhaenger();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      showMessage('error', 'Fehler beim Laden der Anhänger');
    } finally {
      setLoading(false);
    }
  };

  // Hilfsfunktion: SVG einfärben (Fill + Stroke)
  const colorizeSvgText = (svgText, color) => {
    if (!color) return svgText;
    const styleTag = `<style>path, circle, polygon, polyline, ellipse, line, rect { fill: ${color} !important; stroke: ${color} !important; }</style>`;
    if (svgText.includes('<svg')) {
      return svgText.replace(/<svg([^>]*)>/, `<svg$1>${styleTag}`);
    }
    return `${styleTag}${svgText}`;
  };

  // Baue gefärbte Previews für Listenansicht
  useEffect(() => {
    let revoked = [];
    const buildCache = async () => {
      const entries = await Promise.all((items || []).map(async (item) => {
        const svgPath = item.svgPath || item.svgPaths?.[0];
        if (!svgPath) return [item.id, null];
        try {
          const res = await fetch(buildAssetUrl(svgPath));
          if (!res.ok) return [item.id, null];
          const svgText = await res.text();
          const color = item.farben?.[0]?.hexColor || '#d1b08c';
          const colored = colorizeSvgText(svgText, color);
          const url = URL.createObjectURL(new Blob([colored], { type: 'image/svg+xml' }));
          revoked.push(url);
          return [item.id, url];
        } catch (e) {
          return [item.id, null];
        }
      }));
      const map = {};
      entries.forEach(([id, url]) => { map[id] = url; });
      setPreviewCache(map);
    };
    buildCache();
    return () => {
      revoked.forEach(url => URL.revokeObjectURL(url));
    };
  }, [items]);

  // Gefärbte Vorschau für das Formular (abhängig von Farbe + SVG)
  useEffect(() => {
    let url;
    const buildPreview = async () => {
      if (!formData.svgPreview) {
        setColoredFormPreview(null);
        return;
      }
      try {
        const res = await fetch(formData.svgPreview);
        const svgText = await res.text();
        const color = formData.farben?.[0]?.hexColor || '#d1b08c';
        const colored = colorizeSvgText(svgText, color);
        url = URL.createObjectURL(new Blob([colored], { type: 'image/svg+xml' }));
        setColoredFormPreview(url);
      } catch (e) {
        setColoredFormPreview(null);
      }
    };
    buildPreview();
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [formData.svgPreview, formData.farben]);

  const loadRohlingfarben = async () => {
    try {
      const data = await getRohlingfarben();
      setRohlingfarben(Array.isArray(data) ? data : []);
    } catch (error) {
      setRohlingfarben([]);
    }
  };

  const loadGravurfarben = async () => {
    try {
      const data = await getGravurfarben();
      setGravurfarben(Array.isArray(data) ? data : []);
    } catch (error) {
      setGravurfarben([]);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const handleNumberChange = (key, value) => {
    const num = parseFloat(value) || 0;
    setFormData(prev => ({ ...prev, [key]: num }));
  };

  const handleFarbeToggle = (farbeId) => {
    setFormData(prev => {
      const exists = prev.farben.some(f => f.id === farbeId);
      if (exists) {
        return { ...prev, farben: prev.farben.filter(f => f.id !== farbeId) };
      }
      const farbe = rohlingfarben.find(f => f.id === farbeId);
      if (!farbe) return prev;
      return { ...prev, farben: [...prev.farben, farbe] };
    });
  };

  const handleFileChange = (files) => {
    const list = Array.isArray(files) ? files.filter(Boolean) : [];
    const first = list[0];
    if (!first) {
      setFormData(prev => ({ ...prev, svgFiles: [] }));
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, svgFiles: list, svgPreview: reader.result }));
    };
    reader.readAsDataURL(first);
  };

  const handleEdit = (item) => {
    const paths = Array.isArray(item.svgPaths)
      ? item.svgPaths.filter(p => typeof p === 'string' && p.trim())
      : (item.svgPath ? [item.svgPath] : []);
    setEditSvgPaths(paths);
    setFormData({
      name: item.name || '',
      width: item.width || 50,
      height: item.height || 50,
      scale: item.scale || 1,
      gravurX: item.gravurX || 5,
      gravurY: item.gravurY || 5,
      gravurWidth: item.gravurWidth || 40,
      gravurHeight: item.gravurHeight || 20,
      svgFiles: [],
      svgPreview: paths[0] ? buildAssetUrl(paths[0]) : '',
      kategorie: item.kategorie || 'Hunde',
      gravurfarbe: item.gravurfarbe || '',
      farben: item.farben || []
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData(initialForm);
    setEditSvgPaths([]);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editingId) {
      if (!Array.isArray(formData.svgFiles) || formData.svgFiles.length === 0) {
        showMessage('error', 'Bitte mindestens eine SVG-Datei auswählen');
        return;
      }
    } else {
      const hasExisting = Array.isArray(editSvgPaths) && editSvgPaths.length > 0;
      const hasNew = Array.isArray(formData.svgFiles) && formData.svgFiles.length > 0;
      if (!hasExisting && !hasNew) {
        showMessage('error', 'Bitte mindestens eine SVG-Datei behalten oder hochladen');
        return;
      }
    }

    const payload = new FormData();
    payload.append('name', formData.name);
    payload.append('width', formData.width);
    payload.append('height', formData.height);
    payload.append('scale', formData.scale);
    payload.append('gravurX', formData.gravurX);
    payload.append('gravurY', formData.gravurY);
    payload.append('gravurWidth', formData.gravurWidth);
    payload.append('gravurHeight', formData.gravurHeight);
    payload.append('kategorie', formData.kategorie);
    payload.append('gravurfarbe', formData.gravurfarbe || '');
    payload.append('farben', JSON.stringify(formData.farben || []));

    if (editingId && Array.isArray(editSvgPaths)) {
      payload.append('svgPaths', JSON.stringify(editSvgPaths));
    }

    (formData.svgFiles || []).forEach((file) => {
      if (file) payload.append('svgs', file);
    });

    try {
      if (editingId) {
        await updateHolzAnhaenger(editingId, payload);
        showMessage('success', 'Anhänger aktualisiert');
      } else {
        await createHolzAnhaenger(payload);
        showMessage('success', 'Anhänger angelegt');
      }
      resetForm();
      loadItems();
    } catch (error) {
      showMessage('error', 'Speichern fehlgeschlagen (API prüfen)');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Wirklich löschen?')) return;
    try {
      await deleteHolzAnhaenger(id);
      showMessage('success', 'Anhänger gelöscht');
      loadItems();
    } catch (error) {
      showMessage('error', 'Löschen fehlgeschlagen');
    }
  };

  const makeSvgDefault = (index) => {
    setEditSvgPaths((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      if (index <= 0 || index >= list.length) return list;
      const [picked] = list.splice(index, 1);
      setFormData((fd) => {
        if (String(fd.svgPreview || '').startsWith('data:')) return fd;
        return { ...fd, svgPreview: picked ? buildAssetUrl(picked) : '' };
      });
      return [picked, ...list];
    });
  };

  const removeSvgVersion = (index) => {
    setEditSvgPaths((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      if (index < 0 || index >= list.length) return list;
      list.splice(index, 1);
      setFormData((fd) => {
        if (!fd.svgPreview) return fd;
        if (String(fd.svgPreview).startsWith('data:')) return fd;
        const stillAvailable = list.some(p => buildAssetUrl(p) === fd.svgPreview);
        if (stillAvailable) return fd;
        return { ...fd, svgPreview: list[0] ? buildAssetUrl(list[0]) : '' };
      });
      return list;
    });
  };

  const previewWidthPx = formData.width * previewScale * formData.scale;
  const previewHeightPx = formData.height * previewScale * formData.scale;

  const overlayStyle = {
    left: formData.gravurX * previewScale * formData.scale,
    top: formData.gravurY * previewScale * formData.scale,
    width: formData.gravurWidth * previewScale * formData.scale,
    height: formData.gravurHeight * previewScale * formData.scale
  };

  const startDrag = (e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    setDragState({
      mode,
      startX: e.clientX,
      startY: e.clientY,
      startBox: {
        x: formData.gravurX,
        y: formData.gravurY,
        w: formData.gravurWidth,
        h: formData.gravurHeight
      }
    });
  };

  const onDrag = (e) => {
    if (!dragState) return;
    const dxPx = e.clientX - dragState.startX;
    const dyPx = e.clientY - dragState.startY;
    const dxMm = dxPx / (previewScale * formData.scale);
    const dyMm = dyPx / (previewScale * formData.scale);

    if (dragState.mode === 'move') {
      setFormData(prev => ({
        ...prev,
        gravurX: Math.max(0, dragState.startBox.x + dxMm),
        gravurY: Math.max(0, dragState.startBox.y + dyMm)
      }));
    } else if (dragState.mode === 'resize') {
      setFormData(prev => ({
        ...prev,
        gravurWidth: Math.max(5, dragState.startBox.w + dxMm),
        gravurHeight: Math.max(5, dragState.startBox.h + dyMm)
      }));
    }
  };

  const endDrag = () => setDragState(null);

  return (
    <div className="holz-verwaltung" onMouseMove={onDrag} onMouseUp={endDrag} onMouseLeave={endDrag}>
      {message.text && <div className={`alert alert-${message.type}`}>{message.text}</div>}

      <div className="header">
        <h2>Christbaumanhänger (Holz)</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Abbrechen' : '+ Neuer Anhänger'}
        </button>
      </div>

      {showForm && (
        <div className="card holz-form">
          <h3>{editingId ? 'Anhänger bearbeiten' : 'Neuer Anhänger'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Breite (mm)</label>
                <input
                  type="number"
                  min="1"
                  value={formData.width}
                  onChange={(e) => handleNumberChange('width', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Höhe (mm)</label>
                <input
                  type="number"
                  min="1"
                  value={formData.height}
                  onChange={(e) => handleNumberChange('height', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Skalierung</label>
                <input
                  type="range"
                  min="0.2"
                  max="3"
                  step="0.05"
                  value={formData.scale}
                  onChange={(e) => handleNumberChange('scale', e.target.value)}
                />
                <small>{formData.scale.toFixed(2)}x</small>
              </div>
              <div className="form-group">
                <label>Kategorie *</label>
                <select
                  value={formData.kategorie}
                  onChange={(e) => setFormData(prev => ({ ...prev, kategorie: e.target.value }))}
                  required
                >
                  <option value="Hunde">Hunde</option>
                  <option value="Katzen">Katzen</option>
                  <option value="Kaninchen">Kaninchen</option>
                </select>
              </div>
              <div className="form-group">
                <label>Gravurfarbe</label>
                <div className="gravurfarbe-select-wrapper">
                  <select
                    value={formData.gravurfarbe}
                    onChange={(e) => setFormData(prev => ({ ...prev, gravurfarbe: e.target.value }))}
                  >
                    <option value="">Bitte wählen</option>
                    {gravurfarben.map(f => (
                      <option key={f.id} value={f.hexColor}>{f.name} ({f.hexColor})</option>
                    ))}
                  </select>
                  {formData.gravurfarbe && (
                    <span
                      className="color-chip"
                      style={{ backgroundColor: formData.gravurfarbe }}
                      title={formData.gravurfarbe}
                    />
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>SVG-Datei(en)</label>
                <input
                  type="file"
                  accept=".svg"
                  multiple
                  onChange={(e) => handleFileChange(Array.from(e.target.files || []))}
                  required={!editingId}
                />
                {Array.isArray(formData.svgFiles) && formData.svgFiles.length > 0 && (
                  <small className="hint">{formData.svgFiles.length} Datei(en) ausgewählt (werden beim Speichern hinzugefügt)</small>
                )}
              </div>
              <div className="form-group">
                <label>Farben (Palette)</label>
                {rohlingfarben.length === 0 ? (
                  <p className="hint">Keine Farben angelegt.</p>
                ) : (
                  <div className="checkbox-group">
                    {rohlingfarben.map(farbe => {
                      const checked = formData.farben.some(f => f.id === farbe.id);
                      return (
                        <label key={farbe.id} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleFarbeToggle(farbe.id)}
                          />
                          <span
                            className="color-chip"
                            style={{ backgroundColor: farbe.hexColor }}
                            title={farbe.hexColor}
                          />
                          <span>{farbe.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Gravur X (mm)</label>
                <input
                  type="number"
                  value={formData.gravurX}
                  onChange={(e) => handleNumberChange('gravurX', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Gravur Y (mm)</label>
                <input
                  type="number"
                  value={formData.gravurY}
                  onChange={(e) => handleNumberChange('gravurY', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Gravur-Breite (mm)</label>
                <input
                  type="number"
                  value={formData.gravurWidth}
                  onChange={(e) => handleNumberChange('gravurWidth', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Gravur-Höhe (mm)</label>
                <input
                  type="number"
                  value={formData.gravurHeight}
                  onChange={(e) => handleNumberChange('gravurHeight', e.target.value)}
                />
              </div>
            </div>

            {editingId && (
              <div className="form-group svg-versions">
                <label>SVG-Versionen</label>
                {editSvgPaths.length === 0 ? (
                  <p className="hint">Noch keine SVGs vorhanden. Bitte oben hochladen.</p>
                ) : (
                  <div className="svg-versions-list">
                    {editSvgPaths.map((path, idx) => (
                      <div key={`${path}-${idx}`} className="svg-version-row">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setFormData(prev => ({ ...prev, svgPreview: buildAssetUrl(path) }))}
                          title="Vorschau anzeigen"
                        >
                          Version {idx + 1}{idx === 0 ? ' (Standard)' : ''}
                        </button>
                        {idx !== 0 && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => makeSvgDefault(idx)}
                            title="Als Standard setzen"
                          >
                            Als Standard
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeSvgVersion(idx)}
                          title="Diese Version entfernen"
                        >
                          Entfernen
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="holz-preview">
              <div className="preview-frame" style={{ width: previewWidthPx, height: previewHeightPx }}>
                {formData.svgPreview ? (
                  <>
                    <img
                      src={coloredFormPreview || formData.svgPreview}
                      alt="SVG Vorschau"
                      style={{ width: previewWidthPx, height: previewHeightPx, objectFit: 'contain' }}
                    />
                    <div
                      className="gravur-overlay"
                      style={overlayStyle}
                      onMouseDown={(e) => startDrag(e, 'move')}
                    >
                      Gravurfläche
                      <span
                        className="resize-handle"
                        onMouseDown={(e) => startDrag(e, 'resize')}
                      />
                    </div>
                  </>
                ) : (
                  <div className="preview-placeholder">SVG hochladen, um Vorschau zu sehen</div>
                )}
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingId ? 'Aktualisieren' : 'Anlegen'}
              </button>
              {editingId && (
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Abbrechen
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      <div className="holz-list">
        {items.length === 0 && !loading ? (
          <p className="no-data">Keine Anhänger vorhanden.</p>
        ) : (
          items.map(item => (
            <div key={item.id} className="card holz-card">
              <div className="holz-card-header">
                <div>
                  <h3>{item.name}</h3>
                  <p className="dim">{item.width}mm × {item.height}mm</p>
                </div>
                <div className="holz-card-actions">
                  <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(item)}>Bearbeiten</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item.id)}>Löschen</button>
                </div>
              </div>
              <div className="holz-card-body">
                <div className="preview-thumb">
                  {(item.svgPath || item.svgPaths?.[0]) ? (
                    <img
                      src={previewCache[item.id] || buildAssetUrl(item.svgPath || item.svgPaths?.[0])}
                      alt={item.name}
                      style={{ width: 120, height: 120, objectFit: 'contain' }}
                    />
                  ) : (
                    <div className="preview-placeholder small">Keine Vorschau</div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default HolzAnhaengerVerwaltung;
